import React, { useCallback, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, File as FileIcon, X, Box } from "lucide-react";
import { formatBytes } from "@/lib/orderUtils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import STLViewer from "@/components/STLViewer";

const isStl = (name) => /\.stl$/i.test(name || "");

const withRelPath = (file, relPath) => {
  const p = (relPath || file.webkitRelativePath || "").replace(/^\/+/, "");
  if (p && p !== file.name) file.relPath = p;
  return file;
};

const readEntryFiles = (entry, path = "") =>
  new Promise((resolve) => {
    if (entry.isFile) {
      entry.file((f) => resolve([withRelPath(f, path + f.name)]), () => resolve([]));
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const all = [];
      const readBatch = () =>
        reader.readEntries(async (entries) => {
          if (!entries.length) return resolve(all);
          for (const e of entries) all.push(...(await readEntryFiles(e, `${path}${entry.name}/`)));
          readBatch();
        }, () => resolve(all));
      readBatch();
    } else resolve([]);
  });

export default function FileDropzone({ files, setFiles, disabled, allowedExts, typeLabel, hint, maxFiles }) {
  const [dragging, setDragging] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const isAllowed = (name) => {
    if (!allowedExts || allowedExts.length === 0) return true;
    const ext = (name.split(".").pop() || "").toLowerCase();
    return allowedExts.includes(ext);
  };

  const addFiles = useCallback(
    (list) => {
      const arr = Array.from(list);
      const valid = [];
      const invalid = [];
      arr.forEach((f) => (isAllowed(f.name) ? valid : invalid).push(f));
      if (invalid.length) {
        toast.error(`Campo errato: "${invalid.map((f) => f.name).join(", ")}" non ammesso in questa sezione. ${typeLabel || ""}`.trim());
      }
      if (!valid.length) return;
      if (maxFiles) {
        const room = Math.max(0, maxFiles - files.length);
        if (room === 0) {
          toast.error(`Puoi caricare al massimo ${maxFiles} file in questa sezione.`);
          return;
        }
        if (valid.length > room) {
          toast.error(`Limite di ${maxFiles} file: aggiunti solo i primi ${room}.`);
        }
        setFiles((prev) => [...prev, ...valid.slice(0, room)]);
        return;
      }
      setFiles((prev) => [...prev, ...valid]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setFiles, allowedExts, typeLabel, maxFiles, files.length]
  );

  const onDrop = async (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const items = Array.from(e.dataTransfer.items || []);
    const entries = items.map((i) => (i.webkitGetAsEntry ? i.webkitGetAsEntry() : null)).filter(Boolean);
    if (entries.some((en) => en.isDirectory)) {
      const collected = [];
      for (const en of entries) collected.push(...(await readEntryFiles(en)));
      addFiles(collected.filter((f) => !f.name.startsWith(".")));
      return;
    }
    addFiles(e.dataTransfer.files);
  };

  const remove = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div>
      <label
        data-testid="upload-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 text-center cursor-pointer transition-colors ${
          dragging ? "border-teal-500 bg-teal-50" : "border-slate-300 bg-slate-50 hover:border-teal-500"
        }`}
      >
        <UploadCloud className="h-9 w-9 text-teal-600 mb-3" />
        <p className="text-sm font-medium text-slate-700">Carica file</p>
        <p className="text-xs text-slate-500 mt-0.5">Trascina qui file o cartelle (anche non compresse), oppure clicca per selezionare</p>
        <p className="text-xs text-slate-400 mt-1">{hint || "STL, ZIP, XML, immagini, PDF · max 100MB per file"}</p>
        <input
          type="file"
          multiple
          data-testid="file-input"
          className="hidden"
          disabled={disabled}
          accept={allowedExts && allowedExts.length ? allowedExts.map((e) => `.${e}`).join(",") : undefined}
          onChange={(e) => addFiles(e.target.files)}
        />
      </label>


      {files.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="selected-files-list">
          {files.map((f, idx) => (
            <li key={idx} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                {isStl(f.name) ? <Box className="h-4 w-4 text-teal-600 shrink-0" /> : <FileIcon className="h-4 w-4 text-slate-400 shrink-0" />}
                <span className="text-sm text-slate-700 truncate">{f.relPath || f.name}</span>
                <span className="text-xs text-slate-400 shrink-0">{formatBytes(f.size)}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {isStl(f.name) && (
                  <button
                    type="button"
                    data-testid="stl-preview-button"
                    onClick={() => setPreviewFile(f)}
                    className="flex items-center gap-1 rounded-md bg-teal-50 px-2 py-1 text-xs font-medium text-teal-700 hover:bg-teal-100 transition-colors"
                  >
                    <Box className="h-3.5 w-3.5" /> Anteprima 3D
                  </button>
                )}
                {!disabled && (
                  <button type="button" onClick={() => remove(idx)} className="text-slate-400 hover:text-red-500">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!previewFile} onOpenChange={(o) => !o && setPreviewFile(null)}>
        <DialogContent className="max-w-3xl" data-testid="stl-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Box className="h-5 w-5 text-teal-600" /> {previewFile?.name}</DialogTitle>
          </DialogHeader>
          {previewFile && <STLViewer file={previewFile} height={460} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
